import {
  computed,
  getCurrentInstance,
  inject,
  provide,
  type ComputedRef,
  type Ref,
} from 'vue';

/**
 * Injection key for the flag that makes every form input inert, no matter what
 * `properties.editable` says. The view interaction mode alone does not do it: only the
 * header block receives `readonly`, so a model that marks a field as editable keeps it
 * typeable. Read-only screens provide this flag; the builder never does.
 */
export const FORM_BUILDER_FORCED_READONLY_KEY = 'formBuilderForcedReadonly';

/** Provides the forced read-only flag to every element rendered below the canvas. */
export function provideFormBuilderForcedReadonly(isForced: () => boolean | undefined): void {
  provide(
    FORM_BUILDER_FORCED_READONLY_KEY,
    computed(() => isForced() === true)
  );
}

/**
 * Reads the forced read-only flag. No provider, or no component instance at all, means the
 * builder behaviour of today: the element decides on its own `properties.editable`.
 */
export function useFormBuilderForcedReadonly(): ComputedRef<boolean> {
  const forcedReadonly = getCurrentInstance()
    ? inject<Ref<boolean> | null>(FORM_BUILDER_FORCED_READONLY_KEY, null)
    : null;

  return computed(() => forcedReadonly?.value === true);
}

/** Field editability, already discounted by the forced read-only flag. */
export function useEditableField(getEditable: () => boolean | undefined): ComputedRef<boolean> {
  const forcedReadonly = useFormBuilderForcedReadonly();

  return computed(() => getEditable() === true && !forcedReadonly.value);
}
